// ==========================================================
// Shading & Lighting Pipeline for Paint Meister
// Transforms flat 2D coloring into premium artisanal craftwork
// ==========================================================

export type MaterialFinish =
  | 'wood'
  | 'lacquer'
  | 'ceramic'
  | 'metallic'
  | 'leather'
  | 'glass'
  | 'cloth'
  | 'matte';

export class ShadingPipeline {
  /**
   * 3-Pass Carved Ink Outline System
   * 1. Soft deep drop-groove shadow
   * 2. Crisp dark charcoal ink main stroke
   * 3. Subtle top-left light bevel rim
   */
  public static renderCarvedOutline(
    ctx: CanvasRenderingContext2D,
    renderStrokePassFn: () => void,
    baseWidth = 3.5
  ) {
    ctx.save();
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';

    // Pass 1: Soft Carved Groove Shadow (bottom-right offset)
    ctx.save();
    ctx.translate(1.2, 1.6);
    ctx.strokeStyle = 'rgba(10, 14, 20, 0.35)';
    ctx.lineWidth = baseWidth + 2.0;
    renderStrokePassFn();
    ctx.restore();

    // Pass 2: Main Deep Charcoal Ink Stroke
    ctx.save();
    ctx.strokeStyle = '#181e24';
    ctx.lineWidth = baseWidth;
    renderStrokePassFn();
    ctx.restore();

    // Pass 3: Inner Chamfer / Light Bevel Rim (top-left offset)
    ctx.save();
    ctx.translate(-0.8, -0.8);
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.28)';
    ctx.lineWidth = 1.0;
    renderStrokePassFn();
    ctx.restore();

    ctx.restore();
  }

  /**
   * Helper to create rich directional ambient lighting gradient from top-left (135°)
   */
  public static createArtisanGradient(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    baseColor: string,
    finish: MaterialFinish = 'matte'
  ): CanvasGradient {
    const grad = ctx.createLinearGradient(x - w * 0.4, y - h * 0.4, x + w * 0.6, y + h * 0.6);

    // Dynamic contrast based on material finish
    if (finish === 'metallic') {
      grad.addColorStop(0, this.lighten(baseColor, 35));
      grad.addColorStop(0.3, this.lighten(baseColor, 15));
      grad.addColorStop(0.5, baseColor);
      grad.addColorStop(0.8, this.darken(baseColor, 25));
      grad.addColorStop(1, this.darken(baseColor, 40));
    } else if (finish === 'lacquer' || finish === 'ceramic') {
      grad.addColorStop(0, this.lighten(baseColor, 25));
      grad.addColorStop(0.4, baseColor);
      grad.addColorStop(0.85, this.darken(baseColor, 20));
      grad.addColorStop(1, this.darken(baseColor, 35));
    } else if (finish === 'wood' || finish === 'leather') {
      grad.addColorStop(0, this.lighten(baseColor, 18));
      grad.addColorStop(0.5, baseColor);
      grad.addColorStop(1, this.darken(baseColor, 22));
    } else {
      // Standard matte/cloth
      grad.addColorStop(0, this.lighten(baseColor, 12));
      grad.addColorStop(0.6, baseColor);
      grad.addColorStop(1, this.darken(baseColor, 18));
    }

    return grad;
  }

  /**
   * Render soft radial specular highlight glint for polished finishes
   */
  public static renderSpecularGlint(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    rx: number,
    ry: number,
    intensity = 0.5
  ) {
    ctx.save();
    const grad = ctx.createRadialGradient(cx, cy, 2, cx, cy, Math.max(rx, ry));
    grad.addColorStop(0, `rgba(255, 255, 255, ${intensity})`);
    grad.addColorStop(0.4, `rgba(255, 255, 255, ${intensity * 0.4})`);
    grad.addColorStop(1, 'rgba(255, 255, 255, 0)');

    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  /**
   * Color utility: Lighten hex color by percentage
   */
  public static lighten(hex: string, percent: number): string {
    const num = parseInt(hex.replace('#', ''), 16);
    if (isNaN(num)) return hex;
    const r = Math.min(255, Math.floor(((num >> 16) & 255) + (255 - ((num >> 16) & 255)) * (percent / 100)));
    const g = Math.min(255, Math.floor(((num >> 8) & 255) + (255 - ((num >> 8) & 255)) * (percent / 100)));
    const b = Math.min(255, Math.floor((num & 255) + (255 - (num & 255)) * (percent / 100)));
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
  }

  /**
   * Color utility: Darken hex color by percentage
   */
  public static darken(hex: string, percent: number): string {
    const num = parseInt(hex.replace('#', ''), 16);
    if (isNaN(num)) return hex;
    const r = Math.max(0, Math.floor(((num >> 16) & 255) * (1 - percent / 100)));
    const g = Math.max(0, Math.floor(((num >> 8) & 255) * (1 - percent / 100)));
    const b = Math.max(0, Math.floor((num & 255) * (1 - percent / 100)));
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
  }
}
